import { useMemo, useState } from 'react';
import { Box, Button, Chip, TextField, Typography, InputAdornment, Tooltip } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import SearchIcon from '@mui/icons-material/Search';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { notify } from '../../common/functions/react-toastify.function';
import { unlockUserLogin } from './functions/unlock_user_login.function';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : value;
};

export default function LoginLockoutTable({ users, fetchUsers }) {
  const [searchText, setSearchText] = useState('');
  const [unlockingUserId, setUnlockingUserId] = useState(null);

  const validData = Array.isArray(users) ? users : [];

  const filteredData = useMemo(() => {
    if (!searchText.trim()) {
      return validData;
    }
    const query = searchText.trim().toLowerCase();
    return validData.filter((user) =>
      [
        user.user_name,
        user.user_email,
        user.department,
        user.designation,
        user.tenant_name,
        user.last_failed_login_ip,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query))
    );
  }, [validData, searchText]);

  const handleUnlock = (user) => {
    Swal.fire({
      title: 'Unlock user login?',
      html: `Unlock login for <strong>${user.user_name}</strong> (${user.user_email})?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#BD1D23',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Unlock',
    }).then(async (result) => {
      if (!result.isConfirmed) {
        return;
      }
      setUnlockingUserId(user.user_id);
      try {
        const response = await unlockUserLogin(user.user_id);
        if (response) {
          notify('success', response?.message || 'User login unlocked');
          await fetchUsers();
        }
      } finally {
        setUnlockingUserId(null);
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'user_name',
        header: 'Name',
        size: 160,
        Cell: ({ cell }) => <span style={{ color: '#24A0ED' }}>{cell.getValue()}</span>,
      },
      {
        accessorKey: 'user_email',
        header: 'Email',
        size: 200,
      },
      {
        accessorKey: 'department',
        header: 'Department',
        size: 120,
      },
      {
        accessorKey: 'tenant_name',
        header: 'Tenant',
        size: 100,
      },
      {
        accessorKey: 'failed_login_attempts',
        header: 'Failed Attempts',
        size: 120,
      },
      {
        accessorKey: 'last_failed_login_ip',
        header: 'Last Failed IP',
        size: 140,
        Cell: ({ cell }) => cell.getValue() || '—',
      },
      {
        accessorKey: 'login_locked_at',
        header: 'Locked At',
        size: 150,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        id: 'status',
        header: 'Status',
        size: 110,
        Cell: () => (
          <Chip
            icon={<LockIcon sx={{ fontSize: '16px !important' }} />}
            label="Locked"
            size="small"
            sx={{
              backgroundColor: '#FEF2F2',
              color: '#DC2626',
              border: '1px solid #FCA5A5',
              fontWeight: 600,
            }}
          />
        ),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        size: 140,
        enableSorting: false,
        Cell: ({ row }) => {
          const user = row.original;
          const isUnlocking = unlockingUserId === user.user_id;
          return (
            <Tooltip title="Unlock login">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LockOpenIcon />}
                  disabled={isUnlocking}
                  onClick={() => handleUnlock(user)}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#BD1D23',
                    color: '#BD1D23',
                    '&:hover': {
                      borderColor: '#9a191e',
                      backgroundColor: 'rgba(189, 29, 35, 0.04)',
                    },
                  }}
                >
                  {isUnlocking ? 'Unlocking...' : 'Unlock'}
                </Button>
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [unlockingUserId]
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredData,
    initialState: { density: 'compact', pagination: { pageSize: 10, pageIndex: 0 } },
    paginationDisplayMode: 'pages',
    enableBottomToolbar: true,
    enableStickyHeader: true,
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: '#BD1D23',
          color: 'white',
        },
      },
    },
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 380px)' } },
    muiTableBodyCellProps: {
      sx: {
        color: '#667085',
        backgroundColor: '#fff',
        fontSize: '12px',
        height: '40px',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontWeight: 600,
      },
    },
    muiTablePaperProps: { sx: { boxShadow: 'none' } },
    renderTopToolbarCustomActions: () => (
      <TextField
        size="small"
        placeholder="Search locked users..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ minWidth: 280 }}
      />
    ),
  });

  return (
    <Box sx={{ marginRight: '32px', marginTop: '20px' }}>
      {filteredData.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {searchText.trim()
            ? 'No locked users match your search.'
            : 'No users are currently locked due to failed login attempts.'}
        </Typography>
      )}
      <MaterialReactTable table={table} />
    </Box>
  );
}
